import { RelativeDateText } from '@plato/ui-legacy/components/common/relative-date-text';
import { Badge } from '@plato/ui-legacy/components/list-views/badge';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { addDays, formatISO, getWeek, intlFormat, subDays } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { EventsViewModeSelector, type ViewMode } from './events-view-mode-selector.tsx';

export type EventsNavigationProps = {
    day: string;
    viewMode: ViewMode;
    onChangedDay: (day: string) => void;
    onViewModeChange: (mode: ViewMode) => void;
};

export function EventsOverviewNavigation({
    day,
    viewMode,
    onChangedDay,
    onViewModeChange,
}: EventsNavigationProps) {
    const date = new Date(day);
    const { t } = useTranslation();
    const locale = useResolvedLocale();
    const sendEvent = useAnalytics();

    const weekNumber = getWeek(date, { weekStartsOn: 1, firstWeekContainsDate: 4 });

    const handleChange = (newDay: Date, direction: 'previous' | 'next' | 'today') => {
        sendEvent('event_calendar_period_changed', { direction, view_mode: viewMode });
        onChangedDay(formatISO(newDay, { representation: 'date' }));
    };

    // Format calendar week using translation
    const getCalendarWeekText = () => {
        const weekAbbr = t('calendar_week_abbr', { ns: 'events' });
        return `${weekAbbr}${weekNumber}`;
    };

    // Get title based on view mode
    const getViewTitle = () => {
        if (viewMode === 'week') {
            // For week view: show "Month Year" format
            return intlFormat(date, { month: 'long', year: 'numeric' }, { locale });
        } else {
            // For day view: show relative date (Today, Tomorrow, etc.)
            return <RelativeDateText date={date} />;
        }
    };

    return (
        <div className="flex flex-row flex-wrap items-center justify-between gap-3">
            <div className="flex flex-row items-center gap-2">
                <Text size="base" weight="semibold">
                    {getViewTitle()}
                </Text>
                <Badge color="neutral">{getCalendarWeekText()}</Badge>
            </div>
            <div className="flex flex-row items-center gap-3">
                <Button
                    variant="icon"
                    icon={ChevronLeftIcon}
                    onClick={() => {
                        handleChange(subDays(date, viewMode === 'week' ? 7 : 1), 'previous');
                    }}
                />
                <Button
                    variant="icon"
                    icon={ChevronRightIcon}
                    onClick={() => {
                        handleChange(addDays(date, viewMode === 'week' ? 7 : 1), 'next');
                    }}
                />
                <Button
                    variant="subtle"
                    onClick={() => {
                        handleChange(new Date(), 'today');
                    }}
                >
                    {t('today')}
                </Button>
                <EventsViewModeSelector selectedMode={viewMode} onModeChange={onViewModeChange} />
            </div>
        </div>
    );
}
